import { CHATBOOKS_ENV, FUNCTIONS_BASE_URL, ChatbooksEnvironment } from '../config/apiConfig';

export const MIN_PASSWORD_LENGTH = 8;

/** Chatbooks singleSignOnType — Google = 1 (matches web-checkout / iOS). */
export const GOOGLE_SINGLE_SIGN_ON_TYPE = 1;

export interface ChatbooksAuthResult {
  accessToken: string;
  personId: string;
  email: string;
  displayName: string | null;
  environment: ChatbooksEnvironment;
}

interface ChatbooksAuthResponse {
  success?: boolean;
  error?: string;
  accessToken?: string;
  personId?: string;
  email?: string;
  displayName?: string | null;
  environment?: ChatbooksEnvironment;
}

async function postAuth(
  endpoint: string,
  body: Record<string, unknown>,
  fallbackError: string,
): Promise<ChatbooksAuthResult> {
  const response = await fetch(`${FUNCTIONS_BASE_URL}/${endpoint}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

  const data = await response.json() as ChatbooksAuthResponse;

  if (!response.ok || !data.success || !data.accessToken || !data.personId) {
    throw new Error(data.error || fallbackError);
  }

  return {
    accessToken: data.accessToken,
    personId: data.personId,
    email: data.email || String(body.email || '').trim(),
    displayName: data.displayName ?? null,
    environment: data.environment || (body.environment as ChatbooksEnvironment) || CHATBOOKS_ENV,
  };
}

export class ChatbooksAuthAPI {
  static async logon(
    email: string,
    password: string,
    environment: ChatbooksEnvironment = CHATBOOKS_ENV,
  ): Promise<ChatbooksAuthResult> {
    const timeZoneName = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
    const timeZoneSecondsFromGMT = -new Date().getTimezoneOffset() * 60;

    return postAuth('chatbooksAuthLogon', {
      email: email.trim(),
      password,
      environment,
      timeZoneName,
      timeZoneSecondsFromGMT,
    }, 'Sign in failed. Please try again.');
  }

  static async createUser(
    name: string,
    email: string,
    password: string,
    environment: ChatbooksEnvironment = CHATBOOKS_ENV,
  ): Promise<ChatbooksAuthResult> {
    return postAuth('chatbooksAuthCreateUser', {
      name: name.trim(),
      email: email.trim(),
      password,
      environment,
    }, 'Unable to create account. Please try again.');
  }

  static async singleSignOn(
    singleSignOnToken: string,
    singleSignOnType: number,
    profile: { email?: string; name?: string } = {},
    environment: ChatbooksEnvironment = CHATBOOKS_ENV,
  ): Promise<ChatbooksAuthResult> {
    return postAuth('chatbooksAuthSingleSignOn', {
      singleSignOnToken,
      singleSignOnType,
      email: profile.email?.trim(),
      name: profile.name?.trim(),
      environment,
    }, 'Google sign-in failed. Please try again.');
  }
}
